import { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, FolderOpen, Settings2 } from 'lucide-react';
import { DarwinSpinner } from '../components/ui/DarwinSpinner';
import { Button } from '../components/ui/Button';
import { CaseCard } from '../components/cases/CaseCard';
import { CaseFilters } from '../components/cases/CaseFilters';
import { DeleteCaseDialog } from '../components/cases/DeleteCaseDialog';
import { DeletedCaseCard } from '../components/cases/DeletedCaseCard';
import { PermanentDeleteDialog } from '../components/cases/PermanentDeleteDialog';
import { casesApi } from '../lib/api';
import { loadIntakeStatus } from '../components/cases/intakeStatus';
import { cn } from '../components/ui/utils';
import type { Case, CaseStatus } from '../types';
import svgPaths from '../imports/SidebarIconPaths';

const CaseImportWizard = lazy(() => import('../components/cases/CaseImportWizard').then(m => ({ default: m.CaseImportWizard })));
const ManagementModal = lazy(() => import('../components/management/ManagementModal').then(m => ({ default: m.ManagementModal })));

function LogoIcon() {
  return (
    <div className="h-[24.618px] relative shrink-0 w-[26.71px]">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 27 25">
        <path d={svgPaths.p3311a400} fill="#151519" id="Union" />
      </svg>
    </div>
  );
}

function DarwinText() {
  return (
    <div className="h-[19.554px] relative shrink-0 w-[83.253px]">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 84 20">
        <g clipPath="url(#clip0_3_94814)">
          <g id="Group">
            <path d={svgPaths.p8385900} fill="#1B1B1F" id="Vector" />
            <path d={svgPaths.pced9f70} fill="#1B1B1F" id="Vector_2" />
            <path d={svgPaths.p5a1d00} fill="#1B1B1F" id="Vector_3" />
            <path d={svgPaths.p1fb9c9f0} fill="#1B1B1F" id="Vector_4" />
            <path d={svgPaths.p211af00} fill="#1B1B1F" id="Vector_5" />
            <path d={svgPaths.p35c86c00} fill="#1B1B1F" id="Vector_6" />
          </g>
          <path d={svgPaths.pef77900} fill="#151519" id="Vector_7" />
        </g>
        <defs>
          <clipPath id="clip0_3_94814">
            <rect fill="white" height="19.5537" width="83.2533" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}

function RedactitText() {
  return (
    <div className="h-[20.127px] overflow-clip w-[87.699px]">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 88 21">
        <g id="Layer 1">
          <path d={svgPaths.p4752680} fill="#7035C4" id="Vector" />
          <path d={svgPaths.p260cc900} fill="#7035C4" id="Vector_2" />
          <path d={svgPaths.p2e772a00} fill="#7035C4" id="Vector_3" />
          <path d={svgPaths.p3937bc00} fill="#7035C4" id="Vector_4" />
          <g id="Group">
            <path d={svgPaths.p3d000e40} fill="#7035C4" id="Vector_5" />
            <path d={svgPaths.p360c0400} fill="#7035C4" id="Vector_6" />
          </g>
          <path d={svgPaths.pf08f700} fill="#7035C4" id="Vector_7" />
          <path d={svgPaths.p2d2a7a80} fill="#7035C4" id="Vector_8" />
          <path d={svgPaths.pfa6a00} fill="#7035C4" id="Vector_9" />
        </g>
      </svg>
    </div>
  );
}

export function CasesHomePage() {
  const navigate = useNavigate();
  const [cases, setCases] = useState<Case[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  // Create-case wizard (blank case or NextRequest import). Only for creating: an
  // existing case, request-sourced or not, opens straight in the editor.
  const [wizardOpen, setWizardOpen] = useState(false);

  // Delete state
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [deletedCases, setDeletedCases] = useState<Case[]>([]);
  const [isLoadingDeleted, setIsLoadingDeleted] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Case | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState<Case | null>(null);
  const [isPermanentDeleting, setIsPermanentDeleting] = useState(false);

  // Filter state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CaseStatus | 'all'>('all');
  const [ownerFilter, setOwnerFilter] = useState('');

  // Extract unique owners from cases with display names
  const owners = useMemo(() => {
    const ownerMap = new Map<string, string>();
    for (const c of cases) {
      if (!ownerMap.has(c.owner)) {
        ownerMap.set(c.owner, c.owner_name || c.owner);
      }
    }
    return Array.from(ownerMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [cases]);

  // Shared filter predicate for search + status + owner
  const matchesFilters = useCallback(
    (c: Case) => {
      const matchesSearch =
        !search ||
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.notes?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
      const matchesOwner = !ownerFilter || c.owner === ownerFilter;
      return matchesSearch && matchesStatus && matchesOwner;
    },
    [search, statusFilter, ownerFilter],
  );

  // Filter active cases based on search and filters
  const filteredCases = useMemo(() => {
    return cases.filter(matchesFilters);
  }, [cases, matchesFilters]);

  // Deleted cases, filtered the same way — only shown when "Include Deleted" is on
  const filteredDeletedCases = useMemo(() => {
    if (!includeDeleted) return [];
    return deletedCases.filter(matchesFilters);
  }, [deletedCases, includeDeleted, matchesFilters]);

  const totalShown = filteredCases.length + filteredDeletedCases.length;

  // The create-case dialog's NextRequest / eDiscovery status, loaded ahead so
  // the dialog opens showing the right tabs.
  useEffect(() => {
    loadIntakeStatus();
  }, []);

  // Fetch cases on mount
  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await casesApi.getCases({ limit: 100 });
      setCases(response.cases);
    } catch (err) {
      console.error('Failed to fetch cases:', err);
      setError('Failed to load cases. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDeletedCases = async () => {
    setIsLoadingDeleted(true);
    try {
      const response = await casesApi.getDeletedCases({ limit: 100 });
      setDeletedCases(response.cases);
    } catch (err) {
      console.error('Failed to fetch deleted cases:', err);
    } finally {
      setIsLoadingDeleted(false);
    }
  };

  useEffect(() => {
    if (includeDeleted) {
      fetchDeletedCases();
    }
  }, [includeDeleted]);

  const handleDeleteCase = (caseId: string) => {
    const target = cases.find((c) => c.id === caseId);
    if (target) setDeleteTarget(target);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await casesApi.deleteCase(deleteTarget.id);
      setCases((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete case:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestoreCase = async (caseId: string) => {
    try {
      await casesApi.restoreCase(caseId);
      setDeletedCases((prev) => prev.filter((c) => c.id !== caseId));
      fetchCases();
    } catch (err) {
      console.error('Failed to restore case:', err);
    }
  };

  const handlePermanentDelete = (caseId: string) => {
    const target = deletedCases.find((c) => c.id === caseId);
    if (target) setPermanentDeleteTarget(target);
  };

  const handleConfirmPermanentDelete = async () => {
    if (!permanentDeleteTarget) return;
    setIsPermanentDeleting(true);
    try {
      await casesApi.permanentDeleteCase(permanentDeleteTarget.id);
      setDeletedCases((prev) => prev.filter((c) => c.id !== permanentDeleteTarget.id));
      setPermanentDeleteTarget(null);
    } catch (err) {
      console.error('Failed to permanently delete case:', err);
    } finally {
      setIsPermanentDeleting(false);
    }
  };

  const handleCaseClick = (caseId: string) => {
    navigate(`/cases/${caseId}`);
  };

  const handleRenameCase = async (caseId: string, newName: string) => {
    const updated = await casesApi.updateCase(caseId, { name: newName });
    setCases((prev) => prev.map((c) => (c.id === caseId ? { ...c, name: updated.name } : c)));
  };

  const handleCaseCreated = (newCase: Case) => {
    // Add the freshly-created case to the list; the wizard stays open on its
    // data-collection step (Finish navigates to the case view).
    setCases((prev) => (prev.some((c) => c.id === newCase.id) ? prev : [newCase, ...prev]));
  };

  const openCreateWizard = () => {
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <LogoIcon />
              <DarwinText />
              <RedactitText />
            </div>

            {/* Header Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsManageModalOpen(true)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
                aria-label="Global Settings"
                title="Global Settings"
              >
                <Settings2 className="w-5 h-5" />
                Global Settings
              </button>
              <Button onClick={openCreateWizard}>
                <Plus className="h-4 w-4 mr-1" />
                Create Case
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters left + search right */}
        <div className="mb-6">
          <CaseFilters
            search={search}
            onSearchChange={setSearch}
            status={statusFilter}
            onStatusChange={setStatusFilter}
            owner={ownerFilter}
            onOwnerChange={setOwnerFilter}
            owners={owners}
          >
            <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none">
              <button
                type="button"
                role="switch"
                aria-checked={includeDeleted}
                aria-label="Include deleted cases"
                onClick={() => setIncludeDeleted((prev) => !prev)}
                className={cn(
                  "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                  includeDeleted ? 'bg-primary' : 'bg-gray-300'
                )}
              >
                <span
                  className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform",
                    includeDeleted ? 'translate-x-[18px]' : 'translate-x-0.5'
                  )}
                />
              </button>
              <span className={cn(
                "flex items-center gap-1.5 text-sm font-medium transition-colors",
                includeDeleted ? 'text-foreground' : 'text-muted-foreground'
              )}>
                Include Deleted
              </span>
            </label>
          </CaseFilters>
        </div>

        {/* Section title with count */}
        {!isLoading && !error && totalShown > 0 && (
          <div className="mb-4 flex items-baseline gap-2">
            <h2 className="text-lg font-semibold text-foreground">Cases</h2>
            <span className="text-sm font-medium text-muted-foreground">
              {totalShown}
            </span>
          </div>
        )}

        {/* Cases Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <DarwinSpinner size={48} />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-destructive mb-4">{error}</p>
            <Button variant="outline" onClick={fetchCases}>
              Try Again
            </Button>
          </div>
        ) : totalShown === 0 ? (
          <div className="text-center py-20">
            <FolderOpen className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
            {cases.length === 0 ? (
              <>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  No cases yet
                </h3>
                <p className="text-muted-foreground mb-6">
                  Create your first case to get started with document redaction.
                </p>
                <Button
                  onClick={openCreateWizard}
                  className="bg-[#7035C4] hover:bg-[#5c2a9e] text-white"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create Case
                </Button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  No matching cases
                </h3>
                <p className="text-muted-foreground">
                  Try adjusting your search or filter criteria.
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 auto-rows-fr gap-6">
            {filteredCases.map((caseData) => (
              <CaseCard
                key={caseData.id}
                caseData={caseData}
                onClick={handleCaseClick}
                onRename={handleRenameCase}
                onDelete={handleDeleteCase}
              />
            ))}
            {filteredDeletedCases.map((caseData) => (
              <DeletedCaseCard
                key={caseData.id}
                caseData={caseData}
                onRestore={handleRestoreCase}
                onPermanentDelete={handlePermanentDelete}
              />
            ))}
          </div>
        )}

        {/* Inline loader while deleted cases are being fetched */}
        {includeDeleted && isLoadingDeleted && (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading deleted cases…
          </div>
        )}
      </main>

      <Suspense fallback={null}>
        {/* Combined Dictionary + Reasons Management Modal */}
        <ManagementModal
          open={isManageModalOpen}
          onClose={() => setIsManageModalOpen(false)}
        />

        {/* Create-case wizard (blank case or NextRequest import). */}
        {wizardOpen && (
          <CaseImportWizard
            open={wizardOpen}
            onCreated={handleCaseCreated}
            onClose={closeWizard}
            onFinish={(id) => {
              closeWizard();
              navigate(`/cases/${id}`);
            }}
          />
        )}
      </Suspense>

      {/* Delete Case Dialog */}
      <DeleteCaseDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        caseName={deleteTarget?.name ?? ''}
        documentCount={deleteTarget?.document_count ?? 0}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />

      {/* Permanent Delete Dialog */}
      <PermanentDeleteDialog
        open={!!permanentDeleteTarget}
        onOpenChange={(open) => { if (!open) setPermanentDeleteTarget(null); }}
        caseName={permanentDeleteTarget?.name ?? ''}
        onConfirm={handleConfirmPermanentDelete}
        isDeleting={isPermanentDeleting}
      />
    </div>
  );
}
